import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '@common/auth/use-auth';
import {
  actionButtonClassName,
  MediaPageHeaderLayout,
} from '@app/web-player/layout/media-page-header-layout';
import { UserImage } from '@app/web-player/users/user-image';
import { BulletSeparatedItems } from '@app/web-player/layout/bullet-separated-items';
import { Link } from 'react-router-dom';
import { Trans } from '@ui/i18n/trans';
import { ProfileDescription } from '@app/web-player/user-profile/profile-description';
import { User } from '@ui/types/user';
import { Button } from '@ui/buttons/button';
import { DialogTrigger } from '@ui/overlays/dialog/dialog-trigger';
import { EditIcon } from '@ui/icons/material/Edit';
import { EditProfileDialog } from '@app/web-player/user-profile/edit-profile-dialog';
import { FollowButton } from '@common/users/follow-button';
import { VisibilityIcon } from '@ui/icons/material/Visibility';
import { VisibilityOffIcon } from '@ui/icons/material/VisibilityOff';
import { MusicNoteIcon } from '@ui/icons/material/MusicNote';
import { InfoIcon } from '@ui/icons/material/Info';

interface ProfileHeaderProps {
  user: User;
  tabLink: (tabName: string) => string;
}

export function ProfileHeader({ user, tabLink }: ProfileHeaderProps) {
  const { user: currentUser } = useAuth();
  const [isBalanceVisible, setBalanceVisible] = useState(false); // Estado para controle de visibilidade

  const toggleBalanceVisibility = () => {
    setBalanceVisible((prev) => !prev);
  };

  // Função para formatar o saldo como número inteiro
  const formatBalance = (balance: number) => {
    // Converte para número inteiro (arredondando para baixo)
    return Math.floor(balance / 10000).toString();
  };

  return (
    <MediaPageHeaderLayout
      image={
        <UserImage
          user={user}
          size="w-240 h-240"
          className="rounded"
          showProBadge
        />
      }
      title={user.display_name}
      subtitle={
        <>
          {currentUser?.id === user.id && (
            <div className="text-lg text-muted mt-2 flex items-center">
              <button onClick={toggleBalanceVisibility} className="ml-2 mr-3">
                {isBalanceVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </button>
              <MusicNoteIcon className="text-xl mr-2 text-primary" />
              <p className="mr-2">
                {isBalanceVisible ? `${formatBalance(user.wallet_balance ?? 0)} Cifras` : '********'}
              </p>
              {(() => {
                const [showTooltip, setShowTooltip] = React.useState(false);
                useEffect(() => {
                  if (!showTooltip) return;
                  const timer = setTimeout(() => setShowTooltip(false), 10000);
                  return () => clearTimeout(timer);
                }, [showTooltip]);

                return (
                  <span
                    style={{ position: 'relative', display: 'inline-flex' }}
                    tabIndex={0}
                    onBlur={() => setShowTooltip(false)}
                  >
                    <InfoIcon
                      className="text-muted cursor-help"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTooltip((v) => !v);
                      }}
                      aria-label="Informação sobre Cifras"
                    />
                    {showTooltip && (
                      <>
                        <div
                          style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100vw',
                            height: '100vh',
                            zIndex: 99,
                            background: 'transparent',
                          }}
                          onClick={() => setShowTooltip(false)}
                        />
                        {window.innerWidth <= 600 ? (
                          // MOBILE: caixa horizontal, margem lateral, mais próxima do rodapé
                          <div
                            style={{
                              position: 'fixed',
                              left: 16,
                              right: 16,
                              bottom: '49%',
                              background: '#222',
                              color: '#e0e0e0',
                              padding: '12px 10px',
                              borderRadius: '10px',
                              fontSize: '0.75em',
                              zIndex: 100,
                              whiteSpace: 'normal',
                              width: 'auto',
                              maxWidth: 'unset',
                              minWidth: 0,
                              boxShadow: '0 2px 12px rgba(0,0,0,0.22)',
                              textAlign: 'center',
                              lineHeight: '1.3',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            As Cifras são pontos musicais de participação<br />e não representam moeda real ou valor financeiro.
                          </div>
                        ) : (
                          // DESKTOP: caixa longa, duas linhas horizontais
                          <div
                            style={{
                              position: 'absolute',
                              top: '120%',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              background: '#222',
                              color: '#e0e0e0',
                              padding: '10px 28px',
                              borderRadius: '8px',
                              fontSize: '0.75em',
                              zIndex: 100,
                              whiteSpace: 'normal',
                              minWidth: '400px',
                              maxWidth: '600px',
                              boxShadow: '0 2px 12px rgba(0,0,0,0.22)',
                              textAlign: 'center',
                              lineHeight: '1.3',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            As Cifras são pontos musicais de participação<br />e não representam moeda real ou valor financeiro.
                          </div>
                        )}
                      </>
                    )}
                  </span>
                );
              })()}
            </div>
          )}
          <BulletSeparatedItems className="z-20 mx-auto text-sm text-muted mt-2 text-center md:text-left">
            {user.followers_count && user.followers_count > 0 ? (
              <Link to={tabLink('followers')} className="hover:underline">
                <Trans message=":count followers" values={{ count: user.followers_count }} />
              </Link>
            ) : null}
            {user.followed_users_count && user.followed_users_count > 0 ? (
              <Link to={tabLink('following')} className="hover:underline">
                <Trans message="Following :count" values={{ count: user.followed_users_count }} />
              </Link>
            ) : null}
          </BulletSeparatedItems>
        </>
      }
      actionButtons={
        <div className="flex items-center justify-center md:justify-start">
          <FollowButton
            user={user}
            variant="flat"
            color="primary"
            minWidth={null}
            className={actionButtonClassName({ isFirst: true })}
            radius="rounded-full"
          />
          {currentUser?.id === user.id && <EditButton user={user} />}
        </div>
      }
      footer={<ProfileDescription profile={user.profile} links={user.links} />}
    />
  );
}

interface EditButtonProps {
  user: User;
}

function EditButton({ user }: EditButtonProps) {
  return (
    <DialogTrigger type="modal">
      <Button
        variant="outline"
        radius="rounded-full"
        startIcon={<EditIcon />}
        className={actionButtonClassName()}
      >
        <Trans message="Edit" />
      </Button>
      <EditProfileDialog user={user} />
    </DialogTrigger>
  );
}
